import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Avatar,
  Collapse,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PeopleIcon from "@mui/icons-material/People";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import RepFormFields, { RepFormValues } from "./RepFormFields";
import { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import { MuiTelInputInfo } from "mui-tel-input";
import { FieldLabel, StyledTextField } from "../types/company-form";

interface AddRepresentativeDrawerProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  hqBranchId: number; // the HQ branch id to link new reps to
  onSuccess: () => void;
}

interface ContactSearchResult {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  role: string | null;
  contact_image: string | null;
}

type Mode = "idle" | "search" | "create";

const EMPTY_FORM: RepFormValues = {
  firstName: "",
  lastName: "",
  role: "",
  phone: "",
  email: "",
};

function getInitialsFromFullName(name: string) {
  if (!name || !name.trim()) return "?";
  return name
    .trim()
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function debounce<T extends (...args: string[]) => void>(fn: T, delay: number) {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export default function AddRepresentativeDrawer({
  open,
  onClose,
  companyId,

  onSuccess,
}: AddRepresentativeDrawerProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color400 = reduxTheme.primary_colors?.[400] ?? "#8B4513";
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];

  const [mode, setMode] = useState<Mode>("idle");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ContactSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [createForm, setCreateForm] = useState<RepFormValues>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [createPhoto, setCreatePhoto] = useState<File | null>(null);
  const createPhotoInputRef = useRef<HTMLInputElement>(null);

  const [branches, setBranches] = useState<
    { id: number; name: string; city?: string | null }[]
  >([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);

  const [pendingContact, setPendingContact] =
    useState<ContactSearchResult | null>(null);
  const debouncedSearch = useRef(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }
      setSearching(true);
      try {
        const res = await apiService.get<{ results: ContactSearchResult[] }>(
          `api/contacts/contact/?search=${encodeURIComponent(query)}`,
        );
        setSearchResults(res.data.results ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 500),
  ).current;

  useEffect(() => {
    if (!open || !companyId) return;

    setBranchesLoading(true);

    apiService
      .get<{ results: { id: number; name: string; city?: string | null }[] }>(
        `api/companies/company-branch/?company=${companyId}`,
      )
      .then((res) => setBranches(res.data.results ?? []))
      .catch(() => setBranches([]))
      .finally(() => setBranchesLoading(false));
  }, [open, companyId]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleClose = () => {
    setMode("idle");
    setSearchQuery("");
    setSearchResults([]);
    setCreateForm(EMPTY_FORM);
    setPendingContact(null);
    setSelectedBranchId(null);
    setCreatePhoto(null);
    onClose();
  };

  const handleCreateFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreatePhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setCreateForm((prev) => ({ ...prev, phone: value }));
  };

  const handleCreateRoleChange = (role: string) => {
    setCreateForm((prev) => ({ ...prev, role }));
  };

  const handleSelectExisting = (contact: ContactSearchResult) => {
    setPendingContact(contact);
    setSelectedBranchId(null);
  };

  const handleConfirmLink = async () => {
    if (!pendingContact || !selectedBranchId) return;
    setSubmitting(true);
    try {
      await apiService.patch(`api/contacts/contact/${pendingContact.id}/`, {
        company_branch_id: selectedBranchId,
      });
      toast.success("Representative linked successfully!");
      onSuccess();
      handleClose();
    } catch {
      toast.error("Failed to link representative. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveCreate = async () => {
    if (!createForm.firstName.trim()) {
      toast.error("First name is required.");
      return;
    }
    if (!selectedBranchId) {
      toast.error("Please select a branch.");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append(
        "name",
        `${createForm.firstName} ${createForm.lastName}`.trim(),
      );
      formData.append("first_name", createForm.firstName.trim());
      formData.append("last_name", createForm.lastName.trim());
      formData.append("phone_number", createForm.phone);
      formData.append("email", createForm.email);
      formData.append("role", createForm.role);
      formData.append("company_branch_id", String(selectedBranchId));
      if (createPhoto) {
        formData.append("contact_image", createPhoto);
      }

      await apiService.post("api/contacts/contact/", formData);
      toast.success("Representative added successfully!");
      onSuccess();
      handleClose();
    } catch {
      toast.error("Failed to add representative. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  const BranchSelector = () => (
    <Box sx={{ mt: 2 }}>
      <FieldLabel textcolor={color100}>
        Link to Branch{" "}
        {branchesLoading && <CircularProgress size={10} sx={{ ml: 1 }} />}
      </FieldLabel>
      {!branchesLoading && branches.length === 0 && (
        <Typography variant="caption" sx={{ color: color300 }}>
          No branches found for this company.
        </Typography>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.75 }}>
        {branches.map((branch) => (
          <Chip
            key={branch.id}
            label={branch.name + (branch.city ? ` · ${branch.city}` : "")}
            onClick={() =>
              setSelectedBranchId((prev) =>
                prev === branch.id ? null : branch.id,
              )
            }
            variant={selectedBranchId === branch.id ? "filled" : "outlined"}
            sx={{
              borderColor: alpha(color400 ?? "#ccc", 0.4),
              backgroundColor:
                selectedBranchId === branch.id ? color400 : "transparent",
              color: selectedBranchId === branch.id ? "#fff" : color300,
              fontWeight: 600,
              fontSize: "12px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor:
                  selectedBranchId === branch.id
                    ? color400
                    : alpha(color400 ?? "#ccc", 0.08),
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: color900,
          borderRadius: "20px",
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: `linear-gradient(135deg, ${color400} 0%, #c9834a 100%)`,
          borderRadius: "20px 20px 0 0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PeopleIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: "#fff" }}
          >
            Add Representative
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
        {/* Mode toggle */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: mode !== "idle" ? 2.5 : 0,
            p: 1.5,
            borderRadius: "14px",
            backgroundColor: alpha(color300 ?? "#ccc", 0.08),
            border: "1px solid",
            borderColor: alpha(color300 ?? "#ccc", 0.15),
            width: "fit-content",
          }}
        >
          {[
            {
              m: "search" as Mode,
              icon: <SearchIcon sx={{ fontSize: 20 }} />,
              label: "Search Existing",
            },
            {
              m: "create" as Mode,
              icon: <PersonAddAlt1Icon sx={{ fontSize: 20 }} />,
              label: "Create New",
            },
          ].map(({ m, icon, label }) => (
            <Box
              key={m}
              onClick={() => setMode((prev) => (prev === m ? "idle" : m))}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.6,
                cursor: "pointer",
                opacity: mode !== "idle" && mode !== m ? 0.45 : 1,
                transition: "opacity 0.2s ease",
                minWidth: 80,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor:
                    mode === m ? color400 : alpha(color300 ?? "#ccc", 0.15),
                  color: mode === m ? "#fff" : color100,
                  border: "1.5px solid",
                  borderColor:
                    mode === m ? color400 : alpha(color300 ?? "#ccc", 0.25),
                  transition: "all 0.2s ease",
                  boxShadow:
                    mode === m
                      ? `0 4px 12px ${alpha(color400 ?? "#ccc", 0.35)}`
                      : "none",
                }}
              >
                {icon}
              </Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: mode === m ? color100 : color300,
                  letterSpacing: "0.02em",
                  userSelect: "none",
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Search panel */}
        <Collapse in={mode === "search"}>
          <FieldLabel textcolor={color100}>
            Search by name, email or phone
          </FieldLabel>
          <StyledTextField
            placeholder="Search existing contacts..."
            value={searchQuery}
            onChange={handleSearchChange}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
            InputProps={{
              startAdornment: searching ? (
                <CircularProgress size={14} sx={{ mr: 1 }} />
              ) : (
                <SearchIcon
                  sx={{ fontSize: 16, color: "text.disabled", mr: 1 }}
                />
              ),
            }}
          />

          {!pendingContact && searchResults.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              {searchResults.map((contact) => (
                <Box
                  key={contact.id}
                  onClick={() => handleSelectExisting(contact)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.25,
                    borderRadius: "12px",
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: alpha(color300 ?? "#ccc", 0.2),
                    "&:hover": { bgcolor: alpha(color400 ?? "#ccc", 0.06) },
                  }}
                >
                  <Avatar
                    src={contact.contact_image ?? undefined}
                    sx={{ width: 32, height: 32, fontSize: 12 }}
                  >
                    {getInitialsFromFullName(contact.full_name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {contact.full_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {contact.email}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {pendingContact && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: alpha(color400 ?? "#ccc", 0.3),
                bgcolor: alpha(color400 ?? "#ccc", 0.04),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    src={pendingContact.contact_image ?? undefined}
                    sx={{ width: 28, height: 28, fontSize: 11 }}
                  >
                    {getInitialsFromFullName(pendingContact.full_name)}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600}>
                    {pendingContact.full_name}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setPendingContact(null)}
                  sx={{ color: color300 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
              <BranchSelector />
            </Box>
          )}
        </Collapse>

        {/* Create panel */}
        <Collapse in={mode === "create"}>
          <RepFormFields
            values={createForm}
            onChange={handleCreateFormChange}
            onPhoneChange={handleCreatePhoneChange}
            onRoleChange={handleCreateRoleChange}
            color100={color100}
            color300={color300}
            color700={color700}
            color800={color800}
          />
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={createPhoto ? URL.createObjectURL(createPhoto) : undefined}
              sx={{ width: 48, height: 48 }}
            >
              {createForm.firstName
                ? createForm.firstName[0]?.toUpperCase()
                : "?"}
            </Avatar>
            <input
              ref={createPhotoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => setCreatePhoto(e.target.files?.[0] ?? null)}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => createPhotoInputRef.current?.click()}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontSize: 12,
                borderColor: color400,
                color: color400,
              }}
            >
              {createPhoto ? "Change Photo" : "Upload Photo"}
            </Button>
          </Box>
          <BranchSelector />
        </Collapse>
      </Box>

      {(mode === "create" || (mode === "search" && pendingContact)) && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: color900,
            borderRadius: "0 0 20px 20px",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={mode === "create" ? handleSaveCreate : handleConfirmLink}
            disabled={submitting || !selectedBranchId}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 4,
              bgcolor: color400,
              "&:hover": { bgcolor: color400 },
              "&.Mui-disabled": {
                bgcolor: alpha(color400 ?? "#ccc", 0.4),
                color: "#fff",
              },
            }}
          >
            {submitting
              ? "Saving..."
              : mode === "create"
                ? "Add Representative"
                : "Confirm Link"}
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
